import * as amplitude from '@amplitude/analytics-browser';
import { AUCTION_STATUS_LABELS } from '@/utils/auction/auctionStatusMapping';
import { SlbAuctionStatus } from '@/types/auction';

/**
 * Maps a backend `SlbAuctionStatus` to the user-facing label expected in
 * Amplitude (`Disabled` | `Pending` | `Online` | `Closed` | `Deleted`).
 *
 * Returns `''` when the status is `undefined` so callers can fire the event
 * even before auction data has loaded — Amplitude prefers an empty string over
 * a missing property.
 */
export const getAnalyticsAuctionStatus = (status: SlbAuctionStatus | undefined): string => {
    if (status === undefined) {
        return '';
    }
    return AUCTION_STATUS_LABELS[status] ?? '';
};

/** Page Type values used in Amplitude event properties */
export const PAGE_TYPES = {
    AUCTION_BIDDERS: 'Bidders',
    AUCTION_CATALOG: 'Auction Catalog',
    AUCTION_DETAILS: 'Auction Details',
    AUCTION_LIST: 'Auction List',
    AUCTION_PERFORMANCE: 'Auction Performance',
    AUCTION_RESULTS: 'Auction Results',
    AUCTION_WINNERS: 'Winners',
    BIDDER_PROFILE: 'Bidder Profile',
    BULK_ACTIONS: 'Bulk Actions',
    CREATE_AUCTION: 'Create Auction',
    CREATE_LOT: 'Create Lot',
    EDIT_AUCTION: 'Edit Auction',
    EDIT_LOT: 'Edit Lot',
    HOUSE_PROFILE: 'House Profile',
    HOUSE_SETTINGS: 'House Settings',
    IMPORT_LOTS: 'Import Lots',
    LOGIN: 'Login',
    LOT_IMPORT_ERRORS: 'Lot Import Errors',
    LOT_IMPORT_RESULTS: 'Lot Import Results',
    LOT_IMPORT_SUMMARY: 'Lot Import Summary',
    LOT_TRACKER: 'Lot Tracker',
    MANAGE_PHOTOS: 'Manage Photos',
    PHOTO_UPLOADING: 'Photo Upload',
    RESET_PASSWORD: 'Reset Password',
    UPLOAD_PHOTOS: 'Upload Photos',
} as const;

/**
 * Page Section values for House Settings tab-scoped Amplitude events (SP2-5054).
 * Seller ratings moved out of this tab bar to its own route in SP2-5583 — that page's
 * analytics are tracked separately (deferred to SP2-5234, per SP2-5228's plan), not via
 * `trackHouseSettingsPageViewed`.
 */
export const PAGE_SECTIONS = {
    AUTO_APPROVAL_LIST: 'Auto-Approval List',
    AUTO_DECLINE_LIST: 'Auto-Decline List',
    COMPANY_PROFILE: 'Company Profile',
    DEFAULT_FEES: 'Default Fees',
    USER_MANAGEMENT: 'User Management',
} as const;

export type HouseSettingsPageSection = (typeof PAGE_SECTIONS)[keyof typeof PAGE_SECTIONS];

/**
 * Strips a leading locale segment from a pathname (e.g. `/en`, `/en-US`).
 * The lookahead anchor prevents `/house/settings` from becoming `se/settings`
 * when no locale prefix is present. See rule 110 — do not simplify this regex.
 */
const stripLocalePrefixFromPathname = (pathname: string): string =>
    pathname.replace(/^\/[a-z]{2,3}(-[A-Za-z]{2,4})?(?=\/|$)/, '') || '/';

/**
 * Maps a Next.js pathname to an Amplitude Page Type string. Accepts both
 * locale-prefixed paths (e.g. `/en-US/auction/abc/catalog`) and stripped paths
 * (e.g. `/auction/abc/catalog`) — `usePathname()` from `next-intl` already
 * strips the locale, but `window.location.pathname` does not.
 *
 * Returns undefined for unrecognised routes — callers fall back to `'Other'`.
 */
export const getPageTypeFromPathname = (pathname: string): string | undefined => {
    const path = stripLocalePrefixFromPathname(pathname);

    // Home page in main-layout is the auction list
    if (path === '/') {
        return PAGE_TYPES.AUCTION_LIST;
    }

    // More specific auction sub-routes first
    if (/^\/auction\/[^/]+\/lot\/import\/results/.test(path)) {
        return PAGE_TYPES.LOT_IMPORT_RESULTS;
    }
    if (/^\/auction\/[^/]+\/lot\/import\/errors/.test(path)) {
        return PAGE_TYPES.LOT_IMPORT_ERRORS;
    }
    if (/^\/auction\/[^/]+\/lot\/import\/summary\/[^/]+/.test(path)) {
        return PAGE_TYPES.LOT_IMPORT_SUMMARY;
    }
    if (/^\/auction\/[^/]+\/lot\/import/.test(path)) {
        return PAGE_TYPES.IMPORT_LOTS;
    }
    if (/^\/auction\/[^/]+\/lot\/async-import/.test(path)) {
        return PAGE_TYPES.IMPORT_LOTS;
    }
    if (/^\/auction\/[^/]+\/lot\/create/.test(path)) {
        return PAGE_TYPES.CREATE_LOT;
    }
    if (/^\/auction\/[^/]+\/lot\/[^/]+/.test(path)) {
        return PAGE_TYPES.EDIT_LOT;
    }
    if (/^\/auction\/[^/]+\/photos\/uploading/.test(path)) {
        return PAGE_TYPES.PHOTO_UPLOADING;
    }
    if (/^\/auction\/[^/]+\/photos\/upload/.test(path)) {
        return PAGE_TYPES.UPLOAD_PHOTOS;
    }
    if (/^\/auction\/[^/]+\/photos\/manage/.test(path)) {
        return PAGE_TYPES.MANAGE_PHOTOS;
    }
    if (/^\/auction\/[^/]+\/winners/.test(path)) {
        return PAGE_TYPES.AUCTION_WINNERS;
    }
    if (/^\/auction\/[^/]+\/bidders/.test(path)) {
        return PAGE_TYPES.AUCTION_BIDDERS;
    }
    if (/^\/auction\/[^/]+\/bidder\/[^/]+/.test(path)) {
        return PAGE_TYPES.BIDDER_PROFILE;
    }
    if (/^\/auction\/[^/]+\/tracker/.test(path)) {
        return PAGE_TYPES.LOT_TRACKER;
    }
    if (/^\/auction\/[^/]+\/performance/.test(path)) {
        return PAGE_TYPES.AUCTION_PERFORMANCE;
    }
    if (/^\/auction\/[^/]+\/results/.test(path)) {
        return PAGE_TYPES.AUCTION_RESULTS;
    }
    if (/^\/auction\/[^/]+\/catalog/.test(path)) {
        return PAGE_TYPES.AUCTION_CATALOG;
    }
    if (/^\/auction\/[^/]+\/details/.test(path)) {
        return PAGE_TYPES.AUCTION_DETAILS;
    }
    if (/^\/auction\/[^/]+\/edit/.test(path)) {
        return PAGE_TYPES.EDIT_AUCTION;
    }
    if (/^\/auction\/create/.test(path)) {
        return PAGE_TYPES.CREATE_AUCTION;
    }
    if (/^\/auction\/[^/]+\/bulk-actions/.test(path)) {
        return PAGE_TYPES.BULK_ACTIONS;
    }
    if (/^\/auction(\/|$)/.test(path)) {
        return PAGE_TYPES.AUCTION_LIST;
    }

    if (/^\/login/.test(path)) {
        return PAGE_TYPES.LOGIN;
    }
    if (/^\/reset-password/.test(path)) {
        return PAGE_TYPES.RESET_PASSWORD;
    }
    if (/^\/house\/settings/.test(path)) {
        return PAGE_TYPES.HOUSE_SETTINGS;
    }
    if (/^\/house\/profile/.test(path)) {
        return PAGE_TYPES.HOUSE_PROFILE;
    }
    return undefined;
};

/** True when pathname (locale-stripped or not) is the House Settings route. */
export const isHouseSettingsPathname = (pathname: string): boolean =>
    /^\/house\/settings/.test(stripLocalePrefixFromPathname(pathname));

/** Event names sent to Amplitude */
export const AMPLITUDE_EVENTS = {
    ACTIVATE_USER_CLICKED: 'Activate User Clicked',
    ADD_AUTO_APPROVAL_CLICKED: 'Add Auto-Approval Clicked',
    ADD_AUTO_APPROVAL_SUBMITTED: 'Add Auto-Approval Submitted',
    ADD_AUTO_DECLINE_CLICKED: 'Add Auto-Decline Clicked',
    ADD_AUTO_DECLINE_SUBMITTED: 'Add Auto-Decline Submitted',
    ADD_REGISTRATION_COMMENT_CLICKED: 'Add Registration Comment Clicked',
    ADD_SINGLE_LOT_CLICKED: 'Add Single Lot Clicked',
    APPROVE_BIDDER_CLICKED: 'Approve Bidder Clicked',
    APPROVE_CHECKED_BIDDERS_CLICKED: 'Approve Checked Bidders Clicked',
    AUCTION_CREATION_FAILED: 'Auction Creation Failed',
    AUCTION_CREATION_SUCCESSFUL: 'Auction Creation Successful',
    BULK_DELETE_LOTS_CLICKED: 'Bulk Delete Lots Clicked',
    BULK_EDIT_LOT_FIELD_CONFIRMED: 'Bulk Edit Lot Field Confirmed',
    BULK_IMPORT_SUBMITTED: 'Bulk Import Submitted',
    BULK_REMOVE_LOT_FIELD_CONFIRMED: 'Bulk Remove Lot Field Confirmed',
    BULK_UPLOAD_LOTS_CLICKED: 'Bulk Upload Lots Clicked',
    CANCEL_AUCTION_CREATION_CLICKED: 'Cancel Auction Creation Clicked',
    CANCEL_IMPORTED_LOTS_CLICKED: 'Cancel Imported Lots Clicked',
    CANCEL_UPDATE_AUCTION_CLICKED: 'Cancel Update Auction Clicked',
    CREATE_NEW_USER_CLICKED: 'Create New User Clicked',
    CREATE_NEW_USER_SUBMITTED: 'Create New User Submitted',
    DECLINE_BIDDER_CLICKED: 'Decline Bidder Clicked',
    DECLINE_CHECKED_BIDDERS_CLICKED: 'Decline Checked Bidders Clicked',
    DELETE_LOT_CLICKED: 'Delete Lot Clicked',
    DELETE_PHOTOS_CLICKED: 'Delete Photos Clicked',
    DELETE_USER_CLICKED: 'Delete User Clicked',
    DOWNLOAD_WINNERS_CLICKED: 'Download Winners Clicked',
    EDIT_AUCTION_DETAILS_CLICKED: 'Edit Auction Details Clicked',
    EDIT_USER_CLICKED: 'Edit User Clicked',
    EDIT_USER_SUBMITTED: 'Edit User Submitted',
    EXPORT_CATALOG_CLICKED: 'Export Catalog Clicked',
    FILE_INVOICE_REVIEW_CLICKED: 'File Invoice Review Clicked',
    FILE_INVOICE_REVIEW_SUBMITTED: 'File Invoice Review Submitted',
    FILTER_APPLIED: 'Filter Applied',
    LOCK_USER_CLICKED: 'Lock User Clicked',
    LOT_RECONCILIATION_SUBMITTED: 'Lot Reconciliation Submitted',
    MOVE_PHOTOS_CLICKED: 'Move Photos Clicked',
    NEW_AUCTION_CLICKED: 'New Auction Clicked',
    PASS_LOT_CLICKED: 'Pass Lot Clicked',
    PHOTO_UPLOAD_SUBMITTED: 'Photo Upload Submitted',
    REMOVE_AUTO_APPROVAL_CLICKED: 'Remove Auto-Approval Clicked',
    REMOVE_AUTO_DECLINE_CLICKED: 'Remove Auto-Decline Clicked',
    RESET_USER_PASSWORD_CLICKED: 'Reset User Password Clicked',
    RESULTS_SORTED: 'Results Sorted',
    SAVE_COMPANY_PROFILE: 'Save Company Profile',
    SAVE_IMPORTED_LOTS_CLICKED: 'Save Imported Lots Clicked',
    SAVE_IMPORTED_LOTS_FAILED: 'Save Imported Lots Failed',
    SAVE_LOT_CLICKED: 'Save Lot Clicked',
    UPDATE_AUCTION_CLICKED: 'Update Auction Clicked',
    VIEW_CATALOG_ONLINE_CLICKED: 'View Catalog Online Clicked',
} as const;

export type AuctionHouseContext = {
    'ATG Auction House ID': string;
    'Auction House Name': string | undefined;
};

/**
 * Common House Settings context shared by all Settings page Amplitude events.
 * Pass `houseContext['Auction House Name'] ?? ''` at each call site — the track
 * functions send the raw display name to Amplitude (SP2-5054).
 */
export type SettingsHouseContext = Readonly<{
    'ATG Auction House ID': string;
    'Auction House Name': string;
}>;

/**
 * Properties for user management events that include User ID + User Status.
 *
 * `'User ID'` here is the **house user's system ID** (from `HouseUser.id` /
 * `CreateHouseUserResponse.id`), NOT the bidder's accountId. Confirm with
 * Bill Stout during preview validation that Amplitude's tracking plan
 * intentionally reuses `'User ID'` across bidder and house-user contexts.
 */
export type UserManagementWithIdAndStatusTrackingProps = SettingsHouseContext &
    Readonly<{
        'Has Admin Access': boolean;
        'User ID': string;
        'User Status': string;
    }>;

/**
 * Properties for `Create New User Submitted` — has User ID from the API response
 * but no User Status (user is being created).
 *
 * `'User ID'` is the **house user's system ID** returned by `usePostHouseUser`.
 */
export type CreateNewUserSubmittedTrackingProps = SettingsHouseContext &
    Readonly<{
        'Has Admin Access': boolean;
        'User ID': string;
    }>;

/**
 * Properties for Activate User Clicked — no `User ID` or `User Status` (SP2-5054).
 */
export type ActivateUserTrackingProps = SettingsHouseContext &
    Readonly<{
        'Has Admin Access': boolean;
    }>;

/**
 * Properties for Lock User Clicked — no `User ID` (omitted per spec/spreadsheet D4).
 */
export type LockUserTrackingProps = SettingsHouseContext &
    Readonly<{
        'Has Admin Access': boolean;
        'User Status': string;
    }>;

/**
 * Properties for bidder list events that include a Bidder ID.
 *
 * `'Bidder ID'` is the bidder's `accountId`. In the current scaffold (SP2-4906
 * pending) this resolves to the bidder's email address.
 */
export type BidderListSettingsWithBidderIdTrackingProps = SettingsHouseContext &
    Readonly<{
        'Bidder ID': string;
    }>;

/** Properties shared by all Bidder Page interaction events (filter, sort, approve, decline). */
export type BidderPageEventProperties = Readonly<{
    'ATG Auction House ID': string;
    'ATG Auction ID': string;
    'Auction House Name': string | undefined;
}>;

/** Properties for `Download Winners Clicked` on the Auction Winners page (SP2-5122). */
export type DownloadWinnersClickedProperties = BidderPageEventProperties;

export type FilterAppliedProperties = BidderPageEventProperties &
    Readonly<{
        'Filter Type': string;
        'Filter Value': string;
    }>;

export type ResultsSortedProperties = BidderPageEventProperties &
    Readonly<{
        'Sorted By': string;
        // 'Sort Direction' intentionally omitted — users cannot choose direction per spec
    }>;

export type CheckedBiddersActionProperties = BidderPageEventProperties &
    Readonly<{
        'Bidder Count': number;
    }>;

/**
 * Properties for Bidder Profile page approve/decline actions.
 * 'User ID' is the **bidder's** accountId (subject of the action), lower-cased.
 * Distinct from `BidderPageEventProperties` (Bidders list page — that one omits User ID).
 */
export type BidderProfileActionProperties = Readonly<{
    'ATG Auction House ID': string;
    'ATG Auction ID': string;
    'Auction House Name': string | undefined;
    'User ID': string;
}>;

/** Properties for `Add Registration Comment Clicked` — adds the pre-submit status label. */
export type AddRegistrationCommentClickedProperties = BidderProfileActionProperties &
    Readonly<{
        'Auction Registration Status': string;
    }>;

/** Shared house + auction context for Lot Tracker Amplitude events (SP2-5078). */
export type LotTrackerBaseProperties = Readonly<{
    'ATG Auction House ID': string;
    'ATG Auction ID': string;
    'Auction House Name': string | undefined;
}>;

export type LotTrackerLotActionProperties = LotTrackerBaseProperties &
    Readonly<{
        'ATG Lot ID': string;
        Buyer: string;
        'Hammer Price': number;
        Quantity: number;
    }>;

export type LotTrackerReconcileSubmittedProperties = LotTrackerBaseProperties &
    Readonly<{
        'ATG Lot ID': string;
        'Buyer - Initial': string;
        'Buyer - Submitted': string;
        'Hammer Price - Initial': number | null;
        'Hammer Price - Submitted': number | null;
        'Quantity - Initial': number | null;
        'Quantity - Submitted': number | null;
    }>;

export type LotTrackerInvoiceReviewSubmittedProperties = LotTrackerBaseProperties &
    Readonly<{
        'ATG Lot IDs': string[];
        Buyer: string;
        'Buyer Has Item': boolean;
        'Hammer Prices': number[];
        'Has Comment': boolean;
        'Has Contacted Buyer': boolean;
        'Is Declined': boolean;
        'Lot Count': number;
        'Lot Numbers': string[];
        'Review Reason': string;
    }>;

export type InvoiceReviewFormAnalyticsInput = Readonly<{
    buyerDeclineStatus: string;
    buyerHasItem: string;
    comments: string;
    contactStatus: string;
}>;

type AuctionEventProperties = AuctionHouseContext & {
    'ATG Auction ID': string;
    'Auction Status': string;
    'Marketguard Tier'?: string;
};

export type BulkActionsLotEventProperties = AuctionEventProperties &
    Readonly<{
        'ATG Lot IDs': readonly string[];
    }>;

export type BulkEditLotFieldConfirmedProperties = BulkActionsLotEventProperties &
    Readonly<{
        'Lot Field Name': string;
        'Lot Field Value': string;
    }>;

export type BulkRemoveLotFieldConfirmedProperties = BulkActionsLotEventProperties &
    Readonly<{
        'Lot Field Name': string;
    }>;

export type SaveLotClickedProperties = AuctionEventProperties & {
    'ATG Lot ID'?: string;
    'Page Type': (typeof PAGE_TYPES)[keyof typeof PAGE_TYPES];
    'Save Type': 'New' | 'Update';
};

/** Auction Catalog page — shared context for SP2-4928 catalog toolbar/modal events */
export type AuctionCatalogAnalyticsProperties = AuctionEventProperties & {
    'Page Type': typeof PAGE_TYPES.AUCTION_CATALOG;
};

export type BulkImportSubmittedProperties = AuctionCatalogAnalyticsProperties & {
    Filename: string;
    'Has Column Headers': 'True' | 'False';
};

export type DeleteLotClickedProperties = AuctionEventProperties & {
    'ATG Lot ID': string;
    'Page Type': (typeof PAGE_TYPES)[keyof typeof PAGE_TYPES];
};

export type PhotoUploadSubmittedProperties = AuctionEventProperties & {
    'Page Type': typeof PAGE_TYPES.UPLOAD_PHOTOS;
    'Photo Count': number;
    'Total Photo Size in KB': number;
};

export type ImportLotsAnalyticsProperties = AuctionEventProperties & {
    'Error Message'?: string;
    'Lot Count': number;
    'Page Type': typeof PAGE_TYPES.IMPORT_LOTS;
};

/**
 * Track "New Auction" button click on the auction list page.
 */
export const trackNewAuctionClicked = (properties: AuctionHouseContext) => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.NEW_AUCTION_CLICKED, {
            ...properties,
            'Page Type': PAGE_TYPES.AUCTION_LIST,
        });
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track successful auction creation. Call after the API responds successfully.
 */
export const trackAuctionCreationSuccessful = (properties: AuctionEventProperties) => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.AUCTION_CREATION_SUCCESSFUL, {
            ...properties,
            'Page Type': PAGE_TYPES.CREATE_AUCTION,
        });
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track failed auction creation. Call in the catch block of the create flow.
 */
export const trackAuctionCreationFailed = (properties: AuctionHouseContext) => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.AUCTION_CREATION_FAILED, {
            ...properties,
            'Page Type': PAGE_TYPES.CREATE_AUCTION,
        });
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track Cancel button click on the create auction page.
 * ATG Auction ID is optional — the auction does not exist yet at cancel time.
 */
export const trackCancelAuctionCreationClicked = (properties: AuctionHouseContext & { 'ATG Auction ID'?: string }) => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.CANCEL_AUCTION_CREATION_CLICKED, {
            ...properties,
            'Page Type': PAGE_TYPES.CREATE_AUCTION,
        });
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track "Edit details" button click on the auction details page.
 */
export const trackEditAuctionDetailsClicked = (properties: AuctionEventProperties) => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.EDIT_AUCTION_DETAILS_CLICKED, {
            ...properties,
            'Page Type': PAGE_TYPES.AUCTION_DETAILS,
        });
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track "Update Auction" submit button click on the edit auction page.
 * Fires on click (before the API call), not on success.
 */
export const trackUpdateAuctionClicked = (properties: AuctionEventProperties) => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.UPDATE_AUCTION_CLICKED, {
            ...properties,
            'Page Type': PAGE_TYPES.EDIT_AUCTION,
        });
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track Cancel button click on the edit auction page.
 */
export const trackCancelUpdateAuctionClicked = (properties: AuctionEventProperties) => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.CANCEL_UPDATE_AUCTION_CLICKED, {
            ...properties,
            'Page Type': PAGE_TYPES.EDIT_AUCTION,
        });
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track Save on create/edit lot. Call at the start of the validated submit handler
 * (before the create/update API). Omit `ATG Lot ID` for new lots.
 */
export const trackSaveLotClicked = (properties: SaveLotClickedProperties) => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.SAVE_LOT_CLICKED, properties);
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track "Single item" choice in Add item modal on Auction Catalog.
 */
export const trackAddSingleLotClicked = (properties: AuctionCatalogAnalyticsProperties) => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.ADD_SINGLE_LOT_CLICKED, properties);
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track the Bulk Actions toolbar delete click, before the confirmation modal.
 */
export const trackBulkDeleteLotsClicked = (properties: BulkActionsLotEventProperties) => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.BULK_DELETE_LOTS_CLICKED, {
            ...properties,
            'Page Type': PAGE_TYPES.BULK_ACTIONS,
        });
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track one field included in a confirmed Bulk Actions edit submit, before the mutation.
 */
export const trackBulkEditLotFieldConfirmed = (properties: BulkEditLotFieldConfirmedProperties) => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.BULK_EDIT_LOT_FIELD_CONFIRMED, {
            ...properties,
            'Page Type': PAGE_TYPES.BULK_ACTIONS,
        });
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track one blank metadata key included in a confirmed Bulk Actions edit submit, before the mutation.
 */
export const trackBulkRemoveLotFieldConfirmed = (properties: BulkRemoveLotFieldConfirmedProperties) => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.BULK_REMOVE_LOT_FIELD_CONFIRMED, {
            ...properties,
            'Page Type': PAGE_TYPES.BULK_ACTIONS,
        });
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track "Upload CSV" choice in Add item modal on Auction Catalog.
 */
export const trackBulkUploadLotsClicked = (properties: AuctionCatalogAnalyticsProperties) => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.BULK_UPLOAD_LOTS_CLICKED, properties);
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track Continue in Import Catalog modal (validated submit intent, before upload API).
 */
export const trackBulkImportSubmitted = (properties: BulkImportSubmittedProperties) => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.BULK_IMPORT_SUBMITTED, properties);
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track Export catalog menu action on Auction Catalog.
 */
export const trackExportCatalogClicked = (properties: AuctionCatalogAnalyticsProperties) => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.EXPORT_CATALOG_CLICKED, properties);
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track See catalog online menu action on Auction Catalog.
 */
export const trackViewCatalogOnlineClicked = (properties: AuctionCatalogAnalyticsProperties) => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.VIEW_CATALOG_ONLINE_CLICKED, properties);
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track Delete confirmed in delete-lot modal. Callers set `Page Type` to catalog vs edit lot.
 */
export const trackDeleteLotClicked = (properties: DeleteLotClickedProperties) => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.DELETE_LOT_CLICKED, properties);
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track "Import" button clicked with a valid mapping — fires before the API call.
 */
export const trackSaveImportedLotsClicked = (properties: ImportLotsAnalyticsProperties) => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.SAVE_IMPORTED_LOTS_CLICKED, properties);
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track import lots API call failure (thrown or error response).
 */
export const trackSaveImportedLotsFailed = (properties: ImportLotsAnalyticsProperties) => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.SAVE_IMPORTED_LOTS_FAILED, properties);
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track confirmed cancel (user clicked "Leave" in the CancelImportModal).
 */
export const trackCancelImportedLotsClicked = (properties: ImportLotsAnalyticsProperties) => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.CANCEL_IMPORTED_LOTS_CLICKED, properties);
    } catch {
        // Analytics failures must never break the app
    }
};

export type ManagePhotosAnalyticsProperties = AuctionEventProperties & {
    /**
     * Lot number the photos are being moved INTO. Optional — drag-and-drop
     * always supplies it, the modal supplies whatever the user typed.
     */
    'Destination Lot Number'?: string;
    'Page Type': typeof PAGE_TYPES.MANAGE_PHOTOS;
    'Photo Count': number;
    /**
     * Lot number the dragged photo is coming FROM. Only set when the source is
     * unambiguous (single lot). Multi-lot selections in the modal flow omit it.
     */
    'Source Lot Number'?: string;
};

export type DeletePhotosClickedProperties = ManagePhotosAnalyticsProperties & {
    Trigger: 'Lot Menu' | 'Context Menu';
};

/** Delete photos from the Edit Lot page — no Trigger, page type is EDIT_LOT. */
export type EditLotDeletePhotosClickedProperties = AuctionEventProperties & {
    'Page Type': typeof PAGE_TYPES.EDIT_LOT;
    'Photo Count': number;
};

/**
 * Track Move Photos button click in MovePhotosModal (after validation, before API).
 */
export const trackMovePhotosClicked = (properties: ManagePhotosAnalyticsProperties) => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.MOVE_PHOTOS_CLICKED, properties);
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track Delete confirm in DeletePhotosModal (Manage Photos — requires Trigger) or
 * ImageUploaderConfirmationModal (Edit Lot — no Trigger).
 */
export const trackDeletePhotosClicked = (
    properties: DeletePhotosClickedProperties | EditLotDeletePhotosClickedProperties
) => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.DELETE_PHOTOS_CLICKED, properties);
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track photo upload submitted — batch created and user navigated to uploading page.
 */
export const trackPhotoUploadSubmitted = (properties: PhotoUploadSubmittedProperties) => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.PHOTO_UPLOAD_SUBMITTED, properties);
    } catch {
        // Analytics failures must never break the app
    }
};

// --- House Settings analytics helpers (SP2-5054) ---

const formatBooleanForAmplitude = (value: boolean): 'False' | 'True' => (value ? 'True' : 'False');

const buildHouseSettingsBasePayload = (pageSection: HouseSettingsPageSection) => ({
    'Page Section': pageSection,
    'Page Type': PAGE_TYPES.HOUSE_SETTINGS,
});

const buildHouseSettingsContextPayload = (pageSection: HouseSettingsPageSection, properties: SettingsHouseContext) => ({
    ...buildHouseSettingsBasePayload(pageSection),
    'ATG Auction House ID': properties['ATG Auction House ID'],
    'Auction House Name': properties['Auction House Name'],
});

const getAmplitudePageViewProperties = () => {
    if (typeof window === 'undefined') {
        return {};
    }

    const href = window.location.href;
    const url = href.split('?')[0];

    return {
        '[Amplitude] Page Domain': window.location.hostname,
        '[Amplitude] Page Location': href,
        '[Amplitude] Page Path': window.location.pathname,
        '[Amplitude] Page Title': document.title,
        '[Amplitude] Page URL': url,
    };
};

// --- House Settings page view ---

/**
 * Fire `[Amplitude] Page Viewed` on House Settings initial load and tab switches.
 * Pass the active tab's Page Section. Supersedes global provider tracking on this route (SP2-5054).
 */
export const trackHouseSettingsPageViewed = (pageSection: HouseSettingsPageSection): void => {
    try {
        amplitude.track('[Amplitude] Page Viewed', {
            ...getAmplitudePageViewProperties(),
            ...buildHouseSettingsBasePayload(pageSection),
        });
    } catch {
        // Analytics failures must never break the app
    }
};

// --- User Management tab ---

/**
 * Track "Create new user" button click before the drawer opens.
 */
export const trackCreateNewUserClicked = (properties: SettingsHouseContext): void => {
    try {
        amplitude.track(
            AMPLITUDE_EVENTS.CREATE_NEW_USER_CLICKED,
            buildHouseSettingsContextPayload(PAGE_SECTIONS.USER_MANAGEMENT, properties)
        );
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track user creation success. Fires after the API responds with the new user's ID.
 */
export const trackCreateNewUserSubmitted = (properties: CreateNewUserSubmittedTrackingProps): void => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.CREATE_NEW_USER_SUBMITTED, {
            ...buildHouseSettingsContextPayload(PAGE_SECTIONS.USER_MANAGEMENT, properties),
            'Has Admin Access': formatBooleanForAmplitude(properties['Has Admin Access']),
            'User ID': properties['User ID'],
        });
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track "Edit" link click on a user row before the drawer opens.
 */
export const trackEditUserClicked = (properties: UserManagementWithIdAndStatusTrackingProps): void => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.EDIT_USER_CLICKED, {
            ...buildHouseSettingsContextPayload(PAGE_SECTIONS.USER_MANAGEMENT, properties),
            'Has Admin Access': formatBooleanForAmplitude(properties['Has Admin Access']),
            'User ID': properties['User ID'],
            'User Status': properties['User Status'],
        });
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track successful user save. Fires after the PUT API responds.
 */
export const trackEditUserSubmitted = (properties: UserManagementWithIdAndStatusTrackingProps): void => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.EDIT_USER_SUBMITTED, {
            ...buildHouseSettingsContextPayload(PAGE_SECTIONS.USER_MANAGEMENT, properties),
            'Has Admin Access': formatBooleanForAmplitude(properties['Has Admin Access']),
            'User ID': properties['User ID'],
            'User Status': properties['User Status'],
        });
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track "Delete" confirm button click (before the DELETE API call).
 */
export const trackDeleteUserClicked = (properties: UserManagementWithIdAndStatusTrackingProps): void => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.DELETE_USER_CLICKED, {
            ...buildHouseSettingsContextPayload(PAGE_SECTIONS.USER_MANAGEMENT, properties),
            'Has Admin Access': formatBooleanForAmplitude(properties['Has Admin Access']),
            'User ID': properties['User ID'],
            'User Status': properties['User Status'],
        });
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track "Reset password" button click (before the password-reset API call).
 */
export const trackResetUserPasswordClicked = (properties: UserManagementWithIdAndStatusTrackingProps): void => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.RESET_USER_PASSWORD_CLICKED, {
            ...buildHouseSettingsContextPayload(PAGE_SECTIONS.USER_MANAGEMENT, properties),
            'Has Admin Access': formatBooleanForAmplitude(properties['Has Admin Access']),
            'User ID': properties['User ID'],
            'User Status': properties['User Status'],
        });
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track "Activate user" menu item click on a New User row.
 * No `User ID` or `User Status` — omitted per SP2-5054.
 */
export const trackActivateUserClicked = (properties: ActivateUserTrackingProps): void => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.ACTIVATE_USER_CLICKED, {
            ...buildHouseSettingsContextPayload(PAGE_SECTIONS.USER_MANAGEMENT, properties),
            'Has Admin Access': formatBooleanForAmplitude(properties['Has Admin Access']),
        });
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track "Lock user" menu item click on an Active user row.
 * No `User ID` property — omitted per spec (spreadsheet D4).
 */
export const trackLockUserClicked = (properties: LockUserTrackingProps): void => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.LOCK_USER_CLICKED, {
            ...buildHouseSettingsContextPayload(PAGE_SECTIONS.USER_MANAGEMENT, properties),
            'Has Admin Access': formatBooleanForAmplitude(properties['Has Admin Access']),
            'User Status': properties['User Status'],
        });
    } catch {
        // Analytics failures must never break the app
    }
};

// --- Company Profile tab ---

/**
 * Track successful company profile save. Fires after the PUT API responds without error.
 */
export const trackSaveCompanyProfile = (properties: SettingsHouseContext): void => {
    try {
        amplitude.track(
            AMPLITUDE_EVENTS.SAVE_COMPANY_PROFILE,
            buildHouseSettingsContextPayload(PAGE_SECTIONS.COMPANY_PROFILE, properties)
        );
    } catch {
        // Analytics failures must never break the app
    }
};

// --- Auto-Approval List tab ---

/**
 * Track "Add new" button click on the Auto-Approval list tab.
 * No `Bidder ID` — not available at click time (D1).
 */
export const trackAddAutoApprovalClicked = (properties: SettingsHouseContext): void => {
    try {
        amplitude.track(
            AMPLITUDE_EVENTS.ADD_AUTO_APPROVAL_CLICKED,
            buildHouseSettingsContextPayload(PAGE_SECTIONS.AUTO_APPROVAL_LIST, properties)
        );
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track successful bidder add to the auto-approval list.
 * Fires after the API responds — `Bidder ID` is available from the response.
 *
 * Note: In the current scaffold (SP2-4906 pending) `Bidder ID` is the email string.
 */
export const trackAddAutoApprovalSubmitted = (properties: BidderListSettingsWithBidderIdTrackingProps): void => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.ADD_AUTO_APPROVAL_SUBMITTED, {
            ...buildHouseSettingsContextPayload(PAGE_SECTIONS.AUTO_APPROVAL_LIST, properties),
            'Bidder ID': properties['Bidder ID'],
        });
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track "Remove from list" click on an auto-approval row.
 * Fires at click time (before the DELETE API call) to support both mock and live hooks.
 */
export const trackRemoveAutoApprovalClicked = (properties: BidderListSettingsWithBidderIdTrackingProps): void => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.REMOVE_AUTO_APPROVAL_CLICKED, {
            ...buildHouseSettingsContextPayload(PAGE_SECTIONS.AUTO_APPROVAL_LIST, properties),
            'Bidder ID': properties['Bidder ID'],
        });
    } catch {
        // Analytics failures must never break the app
    }
};

// --- Auto-Decline List tab ---

/**
 * Track "Add new" button click on the Auto-Decline list tab.
 * No `Bidder ID` — not available at click time (D1).
 */
export const trackAddAutoDeclineClicked = (properties: SettingsHouseContext): void => {
    try {
        amplitude.track(
            AMPLITUDE_EVENTS.ADD_AUTO_DECLINE_CLICKED,
            buildHouseSettingsContextPayload(PAGE_SECTIONS.AUTO_DECLINE_LIST, properties)
        );
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track successful bidder add to the auto-decline list.
 * Fires after the API responds — `Bidder ID` is available from the response.
 *
 * Note: In the current scaffold (SP2-4906 pending) `Bidder ID` is the email string.
 */
export const trackAddAutoDeclineSubmitted = (properties: BidderListSettingsWithBidderIdTrackingProps): void => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.ADD_AUTO_DECLINE_SUBMITTED, {
            ...buildHouseSettingsContextPayload(PAGE_SECTIONS.AUTO_DECLINE_LIST, properties),
            'Bidder ID': properties['Bidder ID'],
        });
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track "Remove from list" click on an auto-decline row.
 * Fires at click time (before the DELETE API call) to support both mock and live hooks.
 */
export const trackRemoveAutoDeclineClicked = (properties: BidderListSettingsWithBidderIdTrackingProps): void => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.REMOVE_AUTO_DECLINE_CLICKED, {
            ...buildHouseSettingsContextPayload(PAGE_SECTIONS.AUTO_DECLINE_LIST, properties),
            'Bidder ID': properties['Bidder ID'],
        });
    } catch {
        // Analytics failures must never break the app
    }
};

// ─── Bidder Page events (SP2-5021) ───────────────────────────────────────────

/**
 * Track status filter applied on the Auction Bidders page.
 * Fires on user selection; does NOT fire when a filter is cleared (toggled off).
 */
export const trackFilterApplied = (properties: FilterAppliedProperties) => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.FILTER_APPLIED, {
            ...properties,
            'Page Type': PAGE_TYPES.AUCTION_BIDDERS,
        });
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track sort option selected on the Auction Bidders page.
 * Uses the human-readable sort label (e.g. 'Paddle Number'), not the raw sort key.
 * Sort Direction is intentionally omitted — it is not user-selectable per spec.
 */
export const trackResultsSorted = (properties: ResultsSortedProperties) => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.RESULTS_SORTED, {
            ...properties,
            'Page Type': PAGE_TYPES.AUCTION_BIDDERS,
        });
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track Approve Checked Bidders header button clicked on the Auction Bidders page.
 * Fires when the user clicks the checkmark in the status bar, before the confirmation modal opens.
 */
export const trackApproveCheckedBiddersClicked = (properties: CheckedBiddersActionProperties) => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.APPROVE_CHECKED_BIDDERS_CLICKED, {
            ...properties,
            'Page Type': PAGE_TYPES.AUCTION_BIDDERS,
        });
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track Decline Checked Bidders header button clicked on the Auction Bidders page.
 * Fires when the user clicks the X in the status bar, before the confirmation modal opens.
 */
export const trackDeclineCheckedBiddersClicked = (properties: CheckedBiddersActionProperties) => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.DECLINE_CHECKED_BIDDERS_CLICKED, {
            ...properties,
            'Page Type': PAGE_TYPES.AUCTION_BIDDERS,
        });
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track single-row approve button clicked on the Auction Bidders page.
 * Fires before the API call. Bidder ID is intentionally not collected per spec.
 */
export const trackApproveBidderClicked = (properties: BidderPageEventProperties) => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.APPROVE_BIDDER_CLICKED, {
            ...properties,
            'Page Type': PAGE_TYPES.AUCTION_BIDDERS,
        });
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track single-row decline action clicked on the Auction Bidders page.
 * Fires before the API call. Bidder ID is intentionally not collected per spec.
 */
export const trackDeclineBidderClicked = (properties: BidderPageEventProperties) => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.DECLINE_BIDDER_CLICKED, {
            ...properties,
            'Page Type': PAGE_TYPES.AUCTION_BIDDERS,
        });
    } catch {
        // Analytics failures must never break the app
    }
};

// ─── Bidder Profile events (SP2-5728) ────────────────────────────────────────

/**
 * Track Approve button click on the Bidder Profile page.
 * Reuses the `Approve Bidder Clicked` event name from the Bidders list page (SP2-5021)
 * with a different Page Type and an added User ID — see 110-analytics.mdc.
 * Fires before the API call.
 */
export const trackApproveBidderClickedOnProfile = (properties: BidderProfileActionProperties) => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.APPROVE_BIDDER_CLICKED, {
            ...properties,
            'Page Type': PAGE_TYPES.BIDDER_PROFILE,
        });
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track Decline button click on the Bidder Profile page.
 * Reuses the `Decline Bidder Clicked` event name from the Bidders list page (SP2-5021).
 * Fires before the API call.
 */
export const trackDeclineBidderClickedOnProfile = (properties: BidderProfileActionProperties) => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.DECLINE_BIDDER_CLICKED, {
            ...properties,
            'Page Type': PAGE_TYPES.BIDDER_PROFILE,
        });
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track "Add comment" button click in the Registration Log card.
 * Fires before the API call. Never includes the comment text itself (SP2-5728).
 */
export const trackAddRegistrationCommentClicked = (properties: AddRegistrationCommentClickedProperties) => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.ADD_REGISTRATION_COMMENT_CLICKED, {
            ...properties,
            'Page Type': PAGE_TYPES.BIDDER_PROFILE,
        });
    } catch {
        // Analytics failures must never break the app
    }
};

// ─── Lot Tracker events (SP2-5078) ───────────────────────────────────────────

/**
 * Applies Title Case to a string. Used by Lot Tracker and Winners analytics for `Auction House Name`.
 */
const toTitleCase = (str: string): string =>
    str
        .split(/(\s|-)/g)
        .map((part) => (/\w/.test(part) ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : part))
        .join('');

/**
 * Track Download Winners button click on the Auction Winners page header.
 * Fires before the download modal opens (SP2-5122).
 */
export const trackDownloadWinnersClicked = (properties: DownloadWinnersClickedProperties): void => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.DOWNLOAD_WINNERS_CLICKED, {
            'ATG Auction House ID': properties['ATG Auction House ID'],
            'ATG Auction ID': properties['ATG Auction ID'],
            'Auction House Name': toTitleCase(properties['Auction House Name'] ?? ''),
            'Page Type': PAGE_TYPES.AUCTION_WINNERS,
        });
    } catch {
        // Analytics failures must never break the app
    }
};

/** Serializes reconcile numeric fields for Amplitude — null becomes blank (SP2-5120). */
export const formatReconcileNumericForAmplitude = (value: number | null): number | '' => (value === null ? '' : value);

/** Parses reconcile hammer price for analytics payloads. */
export const parseReconcileHammerPriceForAnalytics = (value: string): number | null => {
    if (!value.trim()) {
        return null;
    }

    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? null : parsed;
};

/** Parses reconcile quantity for analytics payloads. */
export const parseReconcileQuantityForAnalytics = (value: string): number | null => {
    if (!value.trim()) {
        return null;
    }

    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? null : parsed;
};

/**
 * Derives boolean invoice-review flags for Amplitude (never includes comment text).
 */
export const toInvoiceReviewAnalyticsFlags = (form: InvoiceReviewFormAnalyticsInput) => ({
    'Buyer Has Item': form.buyerHasItem === 'Yes',
    'Has Comment': form.comments.trim().length > 0,
    'Has Contacted Buyer': form.contactStatus !== 'No, I have not been in contact with the Buyer',
    'Is Declined': form.buyerDeclineStatus !== 'no',
});

const buildLotTrackerBasePayload = (properties: LotTrackerBaseProperties) => ({
    'ATG Auction House ID': properties['ATG Auction House ID'],
    'ATG Auction ID': properties['ATG Auction ID'],
    'Auction House Name': toTitleCase(properties['Auction House Name'] ?? ''),
    'Page Type': PAGE_TYPES.LOT_TRACKER,
});

/**
 * Track successful lot reconciliation save with initial vs submitted form values.
 */
export const trackLotReconciliationSubmitted = (properties: LotTrackerReconcileSubmittedProperties): void => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.LOT_RECONCILIATION_SUBMITTED, {
            ...buildLotTrackerBasePayload(properties),
            'ATG Lot ID': properties['ATG Lot ID'],
            'Buyer - Initial': properties['Buyer - Initial'],
            'Buyer - Submitted': properties['Buyer - Submitted'],
            'Hammer Price - Initial': formatReconcileNumericForAmplitude(properties['Hammer Price - Initial']),
            'Hammer Price - Submitted': properties['Hammer Price - Submitted'],
            'Quantity - Initial': properties['Quantity - Initial'],
            'Quantity - Submitted': properties['Quantity - Submitted'],
        });
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track Pass lot button click on the Lot Tracker reconcile drawer.
 * Fires before form state is updated to PASSED/0/0.
 */
export const trackPassLotClicked = (properties: LotTrackerLotActionProperties): void => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.PASS_LOT_CLICKED, {
            ...buildLotTrackerBasePayload(properties),
            'ATG Lot ID': properties['ATG Lot ID'],
            Buyer: properties.Buyer,
            'Hammer Price': properties['Hammer Price'],
            Quantity: properties.Quantity,
        });
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track File an Invoice Review button click on the Lot Tracker reconcile drawer.
 */
export const trackFileInvoiceReviewClicked = (properties: LotTrackerLotActionProperties): void => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.FILE_INVOICE_REVIEW_CLICKED, {
            ...buildLotTrackerBasePayload(properties),
            'ATG Lot ID': properties['ATG Lot ID'],
            Buyer: properties.Buyer,
            'Hammer Price': properties['Hammer Price'],
            Quantity: properties.Quantity,
        });
    } catch {
        // Analytics failures must never break the app
    }
};

/**
 * Track successful invoice review submission. Comment text is never included — only Has Comment.
 */
export const trackFileInvoiceReviewSubmitted = (properties: LotTrackerInvoiceReviewSubmittedProperties): void => {
    try {
        amplitude.track(AMPLITUDE_EVENTS.FILE_INVOICE_REVIEW_SUBMITTED, {
            ...buildLotTrackerBasePayload(properties),
            'ATG Lot IDs': properties['ATG Lot IDs'],
            Buyer: properties.Buyer,
            'Buyer Has Item': formatBooleanForAmplitude(properties['Buyer Has Item']),
            'Hammer Prices': properties['Hammer Prices'],
            'Has Comment': formatBooleanForAmplitude(properties['Has Comment']),
            'Has Contacted Buyer': formatBooleanForAmplitude(properties['Has Contacted Buyer']),
            'Is Declined': formatBooleanForAmplitude(properties['Is Declined']),
            'Lot Count': properties['Lot Count'],
            'Lot Numbers': properties['Lot Numbers'],
            'Review Reason': properties['Review Reason'],
        });
    } catch {
        // Analytics failures must never break the app
    }
};
